import { BillingInfo } from '@citrite/sf-api';
import { EcommTaxDetails } from '@sharefile/ecomm-models';
import { addressFieldId } from './addressField';
import {
	euroVatCountryCodes,
	gstCountries,
	pstCanadianProvinces,
} from './addressFieldConstants';

const SupportedCountries = ['US'];

/**
 * Verifies whether a country is supported for billing-related actions
 * @param country The country to verify
 * @returns true if the country is supported, false otherwise
 */
export const isCountrySupported = (country?: string): boolean => {
	return SupportedCountries.includes(country || '');
};

/**
 * Check if both the addresses are same.
 * @returns boolean
 */
export const areAddressesSame = (
	address1: BillingInfo,
	address2: BillingInfo
): boolean => {
	if (!address2 && !address1) {
		return true;
	} else if (address2 && address1) {
		return (
			address1.Address1?.trim() === address2.Address1?.trim() &&
			address1.City?.trim() === address2.City?.trim() &&
			address1.State?.trim() === address2.State?.trim() &&
			address1.Zip?.trim() === address2.Zip?.trim() &&
			address1.Country?.trim() === address2.Country?.trim()
		);
	}
	return false;
};

export const sanitizeTaxFields = (
	billingAddress: BillingInfo,
	taxDetails: EcommTaxDetails
): EcommTaxDetails => {
	if (taxDetails.EuropeanVATID) {
		if (
			taxDetails.EuropeanVATID.taxId.length > 0 &&
			euroVatCountryCodes.includes(billingAddress.Country ?? '')
		) {
			taxDetails.EuropeanVATID.taxId = taxDetails.EuropeanVATID.taxId.trim();
		} else {
			taxDetails.EuropeanVATID = undefined;
		}
	}
	if (taxDetails.ABN) {
		if (taxDetails.ABN.taxId.length > 0 && billingAddress.Country === 'AU') {
			taxDetails.ABN.taxId = taxDetails.ABN.taxId.trim();
		} else {
			taxDetails.ABN = undefined;
		}
	}
	if (taxDetails.GSTID) {
		if (
			taxDetails.GSTID.taxId.length > 0 &&
			gstCountries.includes(billingAddress.Country ?? '')
		) {
			taxDetails.GSTID.taxId = taxDetails.GSTID.taxId.trim();
		} else {
			taxDetails.GSTID = undefined;
		}
	}
	if (taxDetails.PSTID) {
		if (
			taxDetails.PSTID.taxId.length > 0 &&
			pstCanadianProvinces.includes(billingAddress.State ?? '')
		) {
			taxDetails.PSTID.taxId = taxDetails.PSTID.taxId.trim();
		} else {
			taxDetails.PSTID = undefined;
		}
	}
	if (taxDetails.QSTID) {
		if (taxDetails.QSTID.taxId.length > 0 && billingAddress.State === 'QC') {
			taxDetails.QSTID.taxId = taxDetails.QSTID.taxId.trim();
		} else {
			taxDetails.QSTID = undefined;
		}
	}
	return taxDetails;
};

export function mapBillingInfoToFormFields(
	billingInfo: BillingInfo,
	taxDetails?: EcommTaxDetails
): Record<string, string | undefined> {
	return {
		[addressFieldId.addressLine1]: billingInfo.Address1,
		[addressFieldId.city]: billingInfo.City,
		[addressFieldId.country]: billingInfo.Country,

		[addressFieldId.state]: billingInfo.State,
		[addressFieldId.province]: billingInfo.State,

		[addressFieldId.postalCode]: billingInfo.Zip,
		[addressFieldId.zipCode]: billingInfo.Zip,

		[addressFieldId.europeanVATId]: taxDetails?.EuropeanVATID?.taxId,
		[addressFieldId.gstHSTNumber]: taxDetails?.GSTID?.taxId,
		[addressFieldId.quebecSalesTax]: taxDetails?.QSTID?.taxId,
		[addressFieldId.provincialSalesTax]: taxDetails?.PSTID?.taxId,
		[addressFieldId.australianBusinessNumber]: taxDetails?.ABN?.taxId,
	};
}

/**
 * Maps form fields to their corresponding billing info properties.
 */
export const handleFormValuesChange = (
	changedValues: Partial<Record<addressFieldId, any>>,
	updateAddressInfo: (addressInfo: BillingInfo, retainInfo?: boolean) => void,
	updateTaxDetails?: (taxDetails: EcommTaxDetails) => void,
	currentCountry = ''
) => {
	for (const changedValue of Object.keys(changedValues)) {
		switch (changedValue as addressFieldId) {
			case addressFieldId.country:
				updateAddressInfo({ Country: changedValues[addressFieldId.country] }, false);
				break;
			case addressFieldId.addressLine1:
				updateAddressInfo({
					Address1: changedValues[addressFieldId.addressLine1],
				});
				break;
			case addressFieldId.city:
				updateAddressInfo({ City: changedValues[addressFieldId.city] });
				break;
			case addressFieldId.zipCode:
			case addressFieldId.postalCode:
				updateAddressInfo({
					Zip:
						changedValues[addressFieldId.zipCode] ||
						changedValues[addressFieldId.postalCode],
				});
				break;
			case addressFieldId.state:
			case addressFieldId.province:
				updateAddressInfo({
					State:
						changedValues[addressFieldId.state] || changedValues[addressFieldId.province],
				});
				break;
			case addressFieldId.europeanVATId:
			case addressFieldId.australianBusinessNumber:
			case addressFieldId.gstHSTNumber:
			case addressFieldId.provincialSalesTax:
			case addressFieldId.quebecSalesTax:
				if (updateTaxDetails) {
					const taxId = changedValues[changedValue as addressFieldId];
					switch (changedValue as addressFieldId) {
						case addressFieldId.europeanVATId:
							updateTaxDetails?.({
								EuropeanVATID: {
									taxId,
									country: currentCountry,
								},
							});
							break;
						case addressFieldId.australianBusinessNumber:
							updateTaxDetails?.({
								ABN: {
									taxId,
									country: currentCountry,
								},
							});
							break;
						case addressFieldId.gstHSTNumber:
							updateTaxDetails?.({
								GSTID: {
									taxId,
									country: currentCountry,
								},
							});
							break;
						case addressFieldId.provincialSalesTax:
							updateTaxDetails?.({
								PSTID: {
									taxId,
									country: currentCountry,
								},
							});
							break;
						case addressFieldId.quebecSalesTax:
							updateTaxDetails?.({
								QSTID: {
									taxId,
									country: currentCountry,
								},
							});
							break;
					}
				}
				break;
			default:
				break;
		}
	}
};
